import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { updateCategory } from "../../../RTK/categories/UpdatecategorySlice";
import { fetchCategories } from "../../../RTK/categories/GetCategories";
import { X, Upload, ChevronDown, Loader2, Eye, EyeOff } from 'lucide-react';

const UpdateCategoryPopup = ({ category, categoryType, isVisible, onClose }) => {
  const dispatch = useDispatch();
  const selectedCategoryType = "quantity";
  const { data } = useSelector((state) => state.categories);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showDropdown, setShowDropdown] = useState(false);
  const [categoryData, setCategoryData] = useState({
    name: "",
    description: "",
    imageFile: null,
    image: null,
    category: "",
    categoryName: "",
    is_hidden: category.is_hidden || false,
  });

  useEffect(() => {
    dispatch(fetchCategories(selectedCategoryType));
  }, [selectedCategoryType, dispatch]);

  useEffect(() => {
    if (category) {
      setCategoryData({
        name: category.name || "",
        description: category.description || category.product_details,
        image: category.image || category.product_image || category.banner_image,
        is_hidden: category.is_hidden || false,
        category: category.category || "",
        categoryName: category.categoryName || category.name || "",
        imageFile: null,
      });
    }
  }, [category]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCategoryData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('يجب أن يكون حجم الصورة أقل من 5 ميجابايت');
        return;
      }
      setCategoryData((prevData) => ({
        ...prevData,
        image: URL.createObjectURL(file),
        imageFile: file,
      }));
    }
  };

  const handleCategorySelection = (cat) => {
    setCategoryData((prev) => ({
      ...prev,
      category: cat.id,
      categoryName: cat.name,
    }));
    setShowDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryData.name || !categoryData.description) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    setIsSubmitting(true);
    const categoryFormData = new FormData();
    categoryFormData.append("name", categoryData.name);

    if (categoryType === "quantity") {
      categoryFormData.append("is_hidden", categoryData.is_hidden);
    }

    if (categoryType === "stock") {
      categoryFormData.append("product_details", categoryData.description);
      categoryFormData.append("product_name", categoryData.name);
      categoryFormData.append("category", categoryData.category);
    } else {
      categoryFormData.append("description", categoryData.description);
    }

    if (categoryType === "package") {
      categoryFormData.append("category", categoryData.category);
    }

    if (categoryType === "package" && categoryData.imageFile) {
      categoryFormData.append("banner_image", categoryData.imageFile);
    } else if (categoryType === "stock" && categoryData.imageFile) {
      categoryFormData.append("product_image", categoryData.imageFile);
    } else if (categoryData.imageFile) {
      categoryFormData.append("image", categoryData.imageFile);
    }

    try {
      await dispatch(updateCategory({
        categoryId: category.id,
        categoryData: categoryFormData,
        categoryType,
      })).unwrap();
      
      toast.success("تم تحديث الفئة بنجاح");
      onClose();
      setCategoryData({
        name: "",
        description: "",
        image: null,
        is_hidden: false,
        category: "",
        categoryName: "",
        imageFile: null,
      });
    } catch (error) {
      toast.error(`خطأ في تحديث الفئة: ${error.message || error}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible || !category) return null;

  const imagePreviewUrl = categoryData.image || category.banner_image || category.product_image || "avatar.png";
  
  const getCategoryTypeName = () => {
    switch(categoryType) {
      case 'quantity': return 'فئة الكمية';
      case 'package': return 'الباقة';
      case 'stock': return 'عنصر المخزون';
      default: return categoryType;
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" dir="rtl">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-gray-900 w-full max-w-2xl rounded-2xl shadow-xl overflow-hidden border border-gray-800 mx-4">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute left-4 top-4 p-2 rounded-full bg-gray-800/50 hover:bg-gray-700/50 transition-colors"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>

        {/* Header */}
        <div className="p-6 border-b border-gray-800">
          <h2 className="text-2xl font-bold text-white">
            تحديث {getCategoryTypeName()}
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Image Upload */}
          <div className="flex flex-col items-center">
            <div className="relative group">
              <img
                src={imagePreviewUrl}
                alt="معاينة"
                className="w-32 h-32 rounded-full object-cover border-2 border-gray-700 group-hover:border-yellow-500 transition-colors"
              />
              <label
                htmlFor="file-upload"
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <Upload className="w-6 h-6 text-white" />
              </label>
            </div>
            <input
              id="file-upload"
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleImageChange}
            />
            <span className="mt-2 text-sm text-gray-400">
              الأبعاد المقترحة: 352 × 192
            </span>
          </div>

          {/* Category Selection */}
          {categoryType !== "quantity" && (
            <div className="relative">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                الفئة
                <span className="text-red-500 mr-1">*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                className="w-full flex items-center justify-between bg-gray-800 text-white px-4 py-2.5 rounded-lg border border-gray-700 hover:border-yellow-500 transition-colors"
              >
                <span>{categoryData.categoryName || "اختر فئة"}</span>
                <ChevronDown className="w-5 h-5 text-gray-400" />
              </button>
              
              {showDropdown && (
                <div className="absolute w-full mt-2 bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden z-[500000]">
                  {Array.isArray(data) && data.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => handleCategorySelection(cat)}
                      className="w-full px-4 py-2.5 text-right hover:bg-gray-700 transition-colors"
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Name Input */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              الاسم
              <span className="text-red-500 mr-1">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={categoryData.name}
              onChange={handleChange}
              className="w-full bg-gray-800 text-white px-4 py-2.5 rounded-lg border border-gray-700 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors"
              placeholder="أدخل اسم الفئة"
            />
          </div>

          {/* Description Input */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              الوصف
              <span className="text-red-500 mr-1">*</span>
            </label>
            <textarea
              name="description"
              value={categoryData.description}
              onChange={handleChange}
              rows="3"
              className="w-full bg-gray-800 text-white px-4 py-2.5 rounded-lg border border-gray-700 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors resize-none"
              placeholder="أدخل وصف الفئة"
            />
          </div>

          

          {/* Visibility Toggle */}
          {categoryType === "quantity" && (
            <div className="flex items-center justify-between bg-gray-800/50 p-4 rounded-lg">
              <div className="flex items-center gap-2">
                {categoryData.is_hidden ? (
                  <EyeOff className="w-5 h-5 text-gray-400" />
                ) : (
                  <Eye className="w-5 h-5 text-gray-400" />
                )}
                <span className="text-gray-300">الظهور</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_hidden"
                  checked={categoryData.is_hidden}
                  onChange={handleChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-yellow-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
              </label>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-4 border-t border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors font-medium"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-yellow-500 text-black hover:bg-yellow-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>جارٍ التحديث...</span>
                </>
              ) : (
                <span>حفظ التغييرات</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateCategoryPopup;